import type {
  Coordinate, DeskShapeFeature, EditableFloorPlan, PolygonFeature,
} from '../models/editor-feature';
import { isDeskOutline } from './desk-outline-shape';

/**
 * Desk drawings that are far too big to be desks, shown as the floor shapes they are.
 *
 * The server marks a floor shape as a desk when it is small and sits beside a desk, and an
 * earlier bulk tagging stored those marks. It got a handful wrong in a way that is plain once
 * the shape is set against the plan's other desks: BDO Enschede's and ArboAnders' L-shaped
 * corners of wall, a table-sized rectangle on BDO Breda, a round shape on StackPros HQ — each
 * three to seven times the size of every real desk drawing on the same plan.
 *
 * The same rule the server's tagger now applies to what it marks, applied here to what was
 * already stored, so the two agree: once one of these is saved as a floor shape, the tagger
 * leaves it one. Nothing is moved or reshaped — the shape only stops being offered as a desk.
 */

/** How much bigger than the plan's typical desk drawing a shape may be, by radius. */
export const MAXIMUM_SIZE_OVER_MEDIAN_DESK = 3;

/** The fewest desk drawings a plan needs before a typical size is taken from them. */
export const MINIMUM_DESK_SHAPES_FOR_A_TYPICAL_SIZE = 5;

/**
 * A drawing's radius: the furthest corner from the average of its corners, over its outline.
 * Measured exactly as the server's tagger measures it, so the two draw the line in one place.
 */
export const deskShapeRadiusOf = (shape: DeskShapeFeature): number => {
  const ring = shape.coordinates[0]?.[0] ?? [];

  if (!ring.length) {
    return 0;
  }

  const first = ring[0];
  const last = ring[ring.length - 1];
  const closed = ring.length > 1 && first[0] === last[0] && first[1] === last[1];
  const corners = closed ? ring.slice(0, -1) : ring;
  const centre: Coordinate = [
    corners.reduce((total, [x]) => total + x, 0) / corners.length,
    corners.reduce((total, [, y]) => total + y, 0) / corners.length,
  ];

  return Math.max(...ring.map(([x, y]) => Math.hypot(x - centre[0], y - centre[1])));
};

const medianOf = (values: readonly number[]): number => {
  const sorted = [...values].sort((first, second) => first - second);
  const middle = Math.floor(sorted.length / 2);

  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
};

/**
 * The plan with these desk drawings turned into plain floor shapes, in the same place and with
 * the same identity. Nothing is moved or reshaped; they only stop being offered as desks.
 */
const demoted = (floorPlan: EditableFloorPlan, shapes: readonly DeskShapeFeature[]): EditableFloorPlan => {
  if (!shapes.length) {
    return floorPlan;
  }

  const asFloor = shapes.map((shape): PolygonFeature => ({
    kind: 'polygon',
    layer: 'floor',
    id: shape.id,
    storedId: shape.storedId,
    featureId: shape.featureId,
    geometryType: 'MultiPolygon',
    coordinates: shape.coordinates,
    rotation: shape.rotation,
    // Left untyped: that is what the shape was before it was mistaken for a desk, and the
    // tagger no longer mistakes it, so nothing more is needed to keep it a floor shape.
    elementType: null,
    centreline: null,
    thickness: null,
    unknownProperties: shape.unknownProperties,
  }));

  return {
    ...floorPlan,
    deskShapes: floorPlan.deskShapes.filter((shape) => !shapes.includes(shape)),
    floorBackdrop: [...floorPlan.floorBackdrop, ...asFloor],
  };
};

/** Whether an administrator has chosen an icon for the shape, which makes it furniture by their say-so. */
const hasChosenIcon = (shape: DeskShapeFeature): boolean => !!shape.iconName;

/** The same plan, with every oversized desk drawing turned into a plain floor shape. */
export const withoutOversizedDeskShapes = (floorPlan: EditableFloorPlan): EditableFloorPlan => {
  if (floorPlan.deskShapes.length < MINIMUM_DESK_SHAPES_FOR_A_TYPICAL_SIZE) {
    return floorPlan;
  }

  const limit = medianOf(floorPlan.deskShapes.map(deskShapeRadiusOf)) * MAXIMUM_SIZE_OVER_MEDIAN_DESK;

  return demoted(
    floorPlan,
    floorPlan.deskShapes.filter((shape) => !hasChosenIcon(shape) && deskShapeRadiusOf(shape) > limit),
  );
};

/**
 * The same plan, with every desk drawing that is not the "[" a desk is drawn as turned into a
 * plain floor shape — unless an icon has been chosen for it.
 *
 * The server's tagger now marks only the "[", so this is for the marks stored before it did: a
 * rectangle, a round shape, a stub of wall. The same rule, so what the tagger would decide and
 * what the editor shows agree.
 */
export const withoutNonDeskOutlines = (floorPlan: EditableFloorPlan): EditableFloorPlan => demoted(
  floorPlan,
  floorPlan.deskShapes.filter((shape) => !hasChosenIcon(shape) && !isDeskOutline(shape.coordinates[0]?.[0] ?? [])),
);

/** Only the desk drawings that are desks: the "[" shapes of a plausible size, and any with a chosen icon. */
export const withOnlyDeskShapes = (floorPlan: EditableFloorPlan): EditableFloorPlan => (
  withoutOversizedDeskShapes(withoutNonDeskOutlines(floorPlan))
);
